import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Lighthouse } from '../types';

interface Props {
  lighthouse: Lighthouse;
  onClose: () => void;
}

interface PhotoRow {
  id: number;
  url: string;
}

const NAVY = '#1b2e4b';

function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <p style={{ margin: '0 0 6px', fontSize: '16px', color: '#000000' }}>
      <span style={{ fontWeight: 700 }}>{label}: </span>
      {value}
    </p>
  );
}

function LighthouseDetail({ lighthouse, onClose }: Props) {
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [photosLoaded, setPhotosLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPhotosLoaded(false);
    setPhotos([]);
    supabase
      .from('visit_photos')
      .select('id, url, visits!inner(lighthouse_id, verified)')
      .eq('visits.lighthouse_id', lighthouse.id)
      .eq('visits.verified', true)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (!cancelled) {
          setPhotos(((data as unknown) as PhotoRow[]) || []);
          setPhotosLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [lighthouse.id]);

  const mapsHref =
    lighthouse.maps_url ||
    `https://www.google.com/maps?q=${lighthouse.latitude},${lighthouse.longitude}`;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(27, 46, 75, 0.55)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          maxWidth: '520px',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          padding: '20px',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '12px',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '24px',
              lineHeight: 1.25,
              color: NAVY,
            }}
          >
            {lighthouse.name}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none',
              background: NAVY,
              color: '#ffffff',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Close
          </button>
        </div>

        <p style={{ margin: '6px 0 14px', fontSize: '16px', color: NAVY, fontWeight: 700 }}>
          {lighthouse.passport_type} passport · {lighthouse.state}
        </p>

        {lighthouse.year_established ? (
          <FactRow label="Established" value={String(lighthouse.year_established)} />
        ) : null}
        {lighthouse.status ? <FactRow label="Status" value={lighthouse.status} /> : null}
        {lighthouse.access ? <FactRow label="Access" value={lighthouse.access} /> : null}
        {lighthouse.amsa ? <FactRow label="AMSA listed" value={lighthouse.amsa} /> : null}

        {lighthouse.notes ? (
          <div style={{ marginTop: '14px' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: '18px', color: NAVY }}>About this light</h3>
            <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.5, color: '#000000' }}>
              {lighthouse.notes}
            </p>
          </div>
        ) : null}

        {lighthouse.access_notes ? (
          <div style={{ marginTop: '14px' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: '18px', color: NAVY }}>Getting there</h3>
            <p
              style={{
                margin: 0,
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#000000',
                whiteSpace: 'pre-line',
              }}
            >
              {lighthouse.access_notes}
            </p>
          </div>
        ) : null}

        <p style={{ margin: '16px 0 0' }}>
          <a
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '16px', fontWeight: 700, color: NAVY }}
          >
            Open in Google Maps
          </a>
        </p>

        <div style={{ marginTop: '18px' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: NAVY }}>
            Member photos{photosLoaded ? ` (${photos.length})` : ''}
          </h3>
          {!photosLoaded ? (
            <p style={{ margin: 0, fontSize: '16px', color: '#000000' }}>Loading photos...</p>
          ) : photos.length === 0 ? (
            <p style={{ margin: 0, fontSize: '16px', color: '#000000' }}>
              No member photos yet. Stamp it and be the first.
            </p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {photos.map((p) => (
                <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={p.url}
                    alt={`Member photo of ${lighthouse.name}`}
                    style={{
                      width: '104px',
                      height: '104px',
                      objectFit: 'cover',
                      borderRadius: '10px',
                      display: 'block',
                    }}
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LighthouseDetail;
